import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  SquaresFour,
  Brain,
  Stack,
  UsersThree,
  Key,
  ShieldCheck,
  SignOut,
  CaretDown,
  Buildings,
  Plus,
  Translate,
  Pulse,
  Gauge,
  ImagesSquare,
  ChartBar,
  PlugsConnected,
  Robot,
  ShareNetwork,
  Storefront,
  EnvelopeSimple,
  ChartLineUp,
  FlowArrow,
  Rocket,
  PaintBrushBroad,
  GoogleLogo,
  LockKey,
  FileText,
  HardDrives,
  Crosshair,
} from "@phosphor-icons/react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useOrg } from "@/context/OrgContext";
import Footer from "@/components/Footer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const NAV = [
  { to: "/dashboard", key: "dashboard", Icon: SquaresFour },
  { to: "/knowledge", key: "knowledge", Icon: Brain },
  { to: "/registry", key: "registry", Icon: Stack },
  { to: "/integrations", key: "integrations", Icon: PlugsConnected },
  { to: "/events", key: "events", Icon: Pulse },
  { to: "/governance", key: "governance", Icon: Gauge },
  { to: "/media", key: "media", Icon: ImagesSquare },
  { to: "/bi", key: "bi", Icon: ChartBar },
  { to: "/ai", key: "ai", Icon: Robot },
  { to: "/social", key: "social", Icon: ShareNetwork },
  { to: "/ecommerce", key: "ecommerce", Icon: Storefront },
  { to: "/promotions", key: "promotions", Icon: Rocket },
  { to: "/creative", key: "creative", label: "Creative Studio", Icon: PaintBrushBroad },
  { to: "/product-hunter", key: "productHunter", testId: "product-hunter", Icon: Crosshair },
  { to: "/email", key: "email", Icon: EnvelopeSimple },
  { to: "/analytics", key: "analytics", Icon: ChartLineUp },
  { to: "/workflows", key: "workflows", Icon: FlowArrow },
  { to: "/members", key: "members", Icon: UsersThree },
  { to: "/settings/api-keys", key: "apiKeys", testId: "api-keys", Icon: Key },
  { to: "/gdpr", key: "gdpr", Icon: ShieldCheck },
  { to: "/settings/google-login", key: "googleLogin", testId: "google-login", Icon: GoogleLogo, adminOnly: true },
  { to: "/settings/security", key: "security", testId: "security", Icon: LockKey, adminOnly: true },
  { to: "/settings/legal", key: "legal", label: "Legal Pages", testId: "legal", Icon: FileText, adminOnly: true },
];

// Platform Administration Center — a visually separate, platform-admin-only section.
// Completely independent from the tenant navigation above.
const PLATFORM_ADMIN_NAV = [
  { to: "/admin/organizations", key: "adminOrgs", testId: "admin-orgs", label: "Organizations", Icon: Buildings },
  { to: "/admin/users", key: "adminUsers", testId: "admin-users", label: "Users", Icon: UsersThree },
  { to: "/admin/system", key: "adminSystem", testId: "admin-system", label: "System", Icon: HardDrives },
  { to: "/admin/ai", key: "adminAI", testId: "admin-ai", label: "AI Providers & Usage", Icon: ChartLineUp },
];

export default function AppLayout() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { orgs, currentOrg, switchOrg, createOrg } = useOrg();
  const navigate = useNavigate();
  const [newOrgOpen, setNewOrgOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleCreateOrg = async () => {
    if (!newOrgName.trim()) return;
    await createOrg(newOrgName.trim());
    setNewOrgName("");
    setNewOrgOpen(false);
  };

  const initials = (user?.name || "U").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border" data-testid="app-header">
        <div className="flex items-center gap-6 px-6 h-16">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary flex items-center justify-center rounded-sm">
              <span className="font-heading font-black text-primary-foreground text-lg leading-none">G</span>
            </div>
            <div className="leading-none">
              <div className="font-heading font-bold tracking-tight text-foreground">{t("brand.name")}</div>
              <div className="overline text-muted-foreground text-[10px]">{t("brand.tagline")}</div>
            </div>
          </div>

          {/* Org switcher */}
          <div className="flex items-center gap-2">
            <Buildings size={16} className="text-muted-foreground" />
            <Select value={currentOrg?.id || ""} onValueChange={switchOrg}>
              <SelectTrigger className="w-[220px] rounded-sm" data-testid="org-switcher">
                <SelectValue placeholder={t("org.switcher")} />
              </SelectTrigger>
              <SelectContent>
                {orgs.map((o) => (
                  <SelectItem key={o.id} value={o.id} data-testid={`org-option-${o.id}`}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Dialog open={newOrgOpen} onOpenChange={setNewOrgOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="icon" className="rounded-sm h-9 w-9" data-testid="new-org-button">
                  <Plus size={16} />
                </Button>
              </DialogTrigger>
              <DialogContent className="backdrop-blur-xl bg-background/80">
                <DialogHeader>
                  <DialogTitle className="font-heading">{t("org.create")}</DialogTitle>
                  <DialogDescription className="sr-only">Create a new organization</DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                  <Label>{t("common.name")}</Label>
                  <Input
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                    data-testid="new-org-name-input"
                  />
                </div>
                <DialogFooter>
                  <Button onClick={handleCreateOrg} data-testid="create-org-submit">
                    {t("common.create")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {/* Language */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="rounded-sm gap-1" data-testid="lang-switcher">
                  <Translate size={16} />
                  <span className="uppercase text-xs font-mono">{i18n.language?.slice(0, 2)}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => i18n.changeLanguage("en")} data-testid="lang-en">English</DropdownMenuItem>
                <DropdownMenuItem onClick={() => i18n.changeLanguage("it")} data-testid="lang-it">Italiano</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1 rounded-sm hover:bg-secondary transition-colors" data-testid="user-menu">
                  <Avatar className="h-7 w-7 rounded-sm">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="rounded-sm text-xs bg-primary text-primary-foreground">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium hidden sm:block">{user?.name}</span>
                  <CaretDown size={12} className="text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{user?.email}</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button" className="text-destructive">
                  <SignOut size={16} className="mr-2" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-56 border-r border-border min-h-[calc(100vh-4rem)] bg-card hidden md:block" data-testid="sidebar">
          <nav className="p-3 space-y-1">
            {NAV.filter((n) => !n.adminOnly || user?.is_platform_admin).map(({ to, key, testId, label, Icon }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`nav-${testId || key}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground font-medium"
                      : "text-secondary-foreground hover:bg-secondary"
                  }`
                }
              >
                <Icon size={18} weight="bold" />
                {label || t(`nav.${key}`)}
              </NavLink>
            ))}

            {/* Platform Administration — separate section, platform-admin only. */}
            {user?.is_platform_admin && (
              <div className="pt-4 mt-3 border-t border-border" data-testid="platform-admin-nav">
                <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Platform Administration
                </p>
                {PLATFORM_ADMIN_NAV.map(({ to, key, testId, label, Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    data-testid={`nav-${testId || key}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                        isActive
                          ? "bg-primary text-primary-foreground font-medium"
                          : "text-secondary-foreground hover:bg-secondary"
                      }`
                    }
                  >
                    <Icon size={18} weight="bold" />
                    {label}
                  </NavLink>
                ))}
              </div>
            )}
          </nav>
        </aside>

        {/* Main */}
        <main className="flex-1 p-6 md:p-8 max-w-[1600px]" data-testid="main-content">
          <Outlet />
        </main>
      </div>
      <Footer />
    </div>
  );
}
